import React, { useEffect, useState } from 'react';
import { Autocomplete, Button, IconButton, TextField } from '@biq/apex-ui';
import CloseIcon from '@mui/icons-material/Close';
import { Card, Grid } from '@mui/material';
import {
    AttributeAssociationItem,
    getEmptyErrorMessage,
} from '../../edge-definition-helper';
import {
    EditAttributeAssociationSelectionProps,
    editAttributeAssociationSelectionStyles,
} from './edit-edge-definition-helper';

export const EditAttributeAssociationSelection = ({
    leftSideSelectionOptions,
    rightSideSelectionOptions,
    handleRemove,
    attributeAssociationItem,
    handleAddItem,
    isFirstItem,
}: EditAttributeAssociationSelectionProps): React.JSX.Element => {
    const [currentItem, setCurrentItem] = useState<AttributeAssociationItem>(
        attributeAssociationItem
    );

    useEffect(() => {
        setCurrentItem(attributeAssociationItem);
    }, [attributeAssociationItem]);

    const handleLeftSideChange = (newValue: string): void => {
        const isLeftSideValid: boolean = !!newValue;
        const isRightSideValid: boolean =
            !!currentItem.attributeAssociation.rightSideValue;
        const displayBothEmpty: boolean = !isLeftSideValid && !isRightSideValid;

        setCurrentItem({
            ...currentItem,
            leftSideError: !newValue,
            leftSideErrorMessage: getEmptyErrorMessage(
                isFirstItem,
                displayBothEmpty,
                !isLeftSideValid
            ),
            rightSideError: !isRightSideValid,
            rightSideErrorMessage: getEmptyErrorMessage(
                isFirstItem,
                displayBothEmpty,
                !isRightSideValid
            ),
            attributeAssociation: {
                ...currentItem.attributeAssociation,
                leftSideValue: newValue,
            },
        });
    };

    const handleRightSideChange = (newValue: string): void => {
        const isRightSideValid: boolean = !!newValue;
        const isLeftSideValid: boolean =
            !!currentItem.attributeAssociation.leftSideValue;
        const displayBothEmpty: boolean = !isLeftSideValid && !isRightSideValid;

        setCurrentItem({
            ...currentItem,
            leftSideError: !isLeftSideValid,
            leftSideErrorMessage: getEmptyErrorMessage(
                isFirstItem,
                displayBothEmpty,
                !isLeftSideValid
            ),
            rightSideError: !newValue,
            rightSideErrorMessage: getEmptyErrorMessage(
                isFirstItem,
                displayBothEmpty,
                !isRightSideValid
            ),
            attributeAssociation: {
                ...currentItem.attributeAssociation,
                rightSideValue: newValue,
            },
        });
    };

    const handleAdd = (): void => {
        handleAddItem(currentItem);
    };

    return (
        <Card sx={editAttributeAssociationSelectionStyles.card}>
            <Grid
                sx={
                    editAttributeAssociationSelectionStyles.attributeAssociationSelectionContainer
                }
            >
                <Grid
                    sx={
                        editAttributeAssociationSelectionStyles.associationLeftSideContainer
                    }
                >
                    <Autocomplete
                        options={leftSideSelectionOptions ?? []}
                        onChange={(_event, newValue): void =>
                            handleLeftSideChange(newValue)
                        }
                        sx={
                            editAttributeAssociationSelectionStyles.associationAutocomplete
                        }
                        renderInput={(params): React.JSX.Element => (
                            <TextField
                                {...params}
                                size='medium'
                                required={true}
                                label='From Attribute'
                                variant='outlined'
                                errorText={currentItem.leftSideErrorMessage}
                                error={currentItem.leftSideError}
                            />
                        )}
                    />
                </Grid>
                <Grid
                    sx={
                        editAttributeAssociationSelectionStyles.associationRightSideContainer
                    }
                >
                    <Autocomplete
                        options={rightSideSelectionOptions ?? []}
                        onChange={(_event, newValue): void =>
                            handleRightSideChange(newValue)
                        }
                        sx={
                            editAttributeAssociationSelectionStyles.associationAutocomplete
                        }
                        renderInput={(params): React.JSX.Element => (
                            <TextField
                                {...params}
                                size='medium'
                                required={true}
                                label='To Attribute'
                                variant='outlined'
                                errorText={currentItem.rightSideErrorMessage}
                                error={currentItem.rightSideError}
                            />
                        )}
                    />
                </Grid>
                <Grid
                    sx={
                        editAttributeAssociationSelectionStyles.buttonsContainer
                    }
                >
                    <Grid
                        sx={
                            editAttributeAssociationSelectionStyles.associationAddButtonContainer
                        }
                    >
                        <Button
                            autoFocus
                            onClick={handleAdd}
                            color='primary'
                            variant='contained'
                            size='small'
                            sx={
                                editAttributeAssociationSelectionStyles.associationAddButton
                            }
                        >
                            Add
                        </Button>
                    </Grid>
                    <Grid
                        sx={
                            editAttributeAssociationSelectionStyles.associationDeleteButtonContainer
                        }
                    >
                        <IconButton
                            color='grayalt'
                            size='small'
                            buttonType='closeButton'
                            onClick={handleRemove}
                            sx={
                                editAttributeAssociationSelectionStyles.associationDeleteButton
                            }
                        >
                            <CloseIcon />
                        </IconButton>
                    </Grid>
                </Grid>
            </Grid>
        </Card>
    );
};
